import { INullable, TObject } from 'utils/types';

import { CALCULATION_VARIANTS, LIMIT_TYPE } from '../data/constants';
import { TResult } from '../manualTransaction/types';
import {
  EXIT_IFRAME_MODE,
  TRANSACTION_STATUS,
  TRANSACTIONS_STATE,
  TRANSACTIONS_TYPE,
} from '../transactions/constants';

import {
  CARDHOLDER_OVERRIDE_TYPES,
  COPY_LIMITS_TARGET_STATUS,
  COUNT_OF,
  INTEGRATION_REQUIRED_CLASS_TYPE,
  INTEGRATION_REQUIRED_INPUT_CLASS_MAP,
  INTEGRATION_REQUIRED_INPUT_TYPE,
  INTEGRATION_REQUIRED_INPUT_TYPE_MAP,
  INTEGRATION_REQUIRED_PARAMETER_TYPE,
  INTEGRATION_REQUIRED_PARAMETER_TYPE_MAP,
  ISSUING_ORG_OPERATION,
  UNIQUE_CUSTOMER_ID_TYPES,
} from './constants';
import {
  ICopyTLLimitsResult,
  ICopyTLLimitsTarget,
  ICopyTLLimitsWarning,
  IDropDownValue,
  IGetTerminalLinksConfig,
  IGetTLGroupTerminalLinksResult,
  ILocalDropDownValue,
  IRequiredParameterData,
  IRequiredParameters,
  IRequiredParametersData,
  IRequiredParameterView,
  ITerminalGroup,
  ITerminalGroupData,
  ITerminalGroupLimits,
  ITerminalLink,
  ITerminalLinkAction,
  ITerminalLinkData,
  ITerminalLinkGroup,
  ITerminalLinkHealth,
  ITerminalLinkHealthBlock,
  ITerminalLinkHealthCounts,
  ITerminalLinkHealthData,
  ITerminalLinkLimit,
  ITerminalLinkParameter,
  ITerminalLinksGroups,
  ITerminalLinksLimits,
  ITerminalLinksLimitsData,
  ITerminalLinksParameters,
  ITerminalsLinks,
  ITerminalsLinksData,
  ITLGroupUniqueCustomerIdCounts,
  ITLParameterLevelValue,
  ITLParameterLowerLevelValues,
  ITLParametersNames,
  ITLSettlementConfig,
  ITLSettlementConfigurations,
  ITLSettlementContract,
  ITrustedSelectOption,
  TERMINAL_LINK_HEALTH_STATUS,
  TTLActionData,
  TTLSettlementConfigData,
  TTLSettlementContractData,
} from './types';

const normalizeTerminalLinkGroup = (data: ITerminalLinkGroup): ITerminalLinkGroup => {
  return {
    id: (data?.id as number) || 0,
    name: (data?.name as string) || '',
  };
};

const normalizeTLParameterLevelValue = (data: TObject): ITLParameterLevelValue => {
  return {
    hasEmptyValue: !!data?.hasEmptyValue,
    hasOwnValue: !!data?.hasOwnValue,
    level: (data?.level as string) || (data?.sourceLevel as string) || '',
    value: (data?.value as string) ?? '',
  };
};

export const normalizeTLParameterLowerLevelValues = (
  data: TObject,
): ITLParameterLowerLevelValues => {
  const source = ((data?.result as TObject) || data || {}) as TObject;
  const rawLevels = (source?.levels || source?.values || []) as TObject[];

  return {
    dataType: (source?.dataType as string) || '',
    levels: rawLevels.map(normalizeTLParameterLevelValue),
    name: (source?.name as string) || '',
  };
};

export const normalizeTerminalsLinks = (data: ITerminalsLinksData): ITerminalsLinks => {
  return {
    bankTerminal: {
      bank: {
        adapterCode: (data?.bankTerminal?.bank?.adapterCode as string) || '',
        id: (data?.bankTerminal?.bank?.id as number) || 0,
        name: (data?.bankTerminal?.bank?.name as string) || '',
        paymentMethod: (data?.bankTerminal?.bank?.paymentMethod as string) || '',
      },
      id: (data?.bankTerminal?.id as number) || 0,
      name: (data?.bankTerminal?.name as string) || '',
    },
    cardholderOverrideMinLength: (data?.cardholderOverrideMinLength as string) || '',
    cardholderOverrideType:
      (data?.cardholderOverrideType as CARDHOLDER_OVERRIDE_TYPES) || '',
    checkStatusOnEarlyWebhook: !!data?.checkStatusOnEarlyWebhook,
    checkStatusOnEarlyWebhookDelaySeconds:
      (data?.checkStatusOnEarlyWebhookDelaySeconds as number) || null,
    customParametersDeposit: (data?.customParametersDeposit as string) || '',
    customParametersPayout: (data?.customParametersPayout as string) || '',
    description: (data?.description as string) || '',
    enabled: !!data?.enabled,
    exitIFrame: !!data?.exitIFrame,
    exitIFrameMode:
      (data?.exitIFrameMode as EXIT_IFRAME_MODE) || EXIT_IFRAME_MODE.CURRENT_TAB,
    hasSettlementConfig: !!data?.hasSettlementConfig,
    id: (data?.id as number) || 0,
    limitsEnabled: !!data?.limitsEnabled,
    merchantTerminal: {
      id: (data?.merchantTerminal?.id as number) || 0,
      merchant: {
        id: (data?.merchantTerminal?.merchant?.id as number) || 0,
        name: (data?.merchantTerminal?.merchant?.name as string) || '',
      },
      name: (data?.merchantTerminal?.name as string) || '',
    },
    motoEnabled: !!data?.motoEnabled,
    multipleFinalStates: !!data?.multipleFinalStates,
    payoutTimeout: (data?.payoutTimeout as number) || null,
    proxyUrl: (data?.proxyUrl as string) || '',
    pspSendPaymentTimeoutSeconds: (data?.pspSendPaymentTimeoutSeconds as number) || null,
    purchaseTimeout: (data?.purchaseTimeout as number) || null,
    recurringEnabled: !!data?.recurringEnabled,
    refundTimeout: (data?.refundTimeout as number) || null,
    settlementContract: {
      contractName: (data?.settlementContract?.contractName as string) || '',
      id: (data?.settlementContract?.id as number) || 0,
    },
    subPaymentMethod: (data?.subPaymentMethod as string) || null,
    terminalLinkGroups: data?.terminalLinkGroups?.map(normalizeTerminalLinkGroup) || [],
    threeDSEnabled: !!data?.threeDSEnabled,
    trusted: !!data?.trusted,
    uniqueCustomerId: (data?.uniqueCustomerId as UNIQUE_CUSTOMER_ID_TYPES) || '',
    useBankMessage: !!data?.useBankMessage,
    useCustomActions: !!data?.useCustomActions,
    useCustomReturnUrl: !!data?.useCustomReturnUrl,
    webhookIpWhitelistEnabled: !!data?.webhookIpWhitelistEnabled,
    webhookProxyUrl: (data?.webhookProxyUrl as string) || '',
    webhooksEnabled: !!data?.webhooksEnabled,
    whitelistIps: ((data?.whitelistIps as string)?.split(';') as string[]) || null,
  };
};

const normalizeParameters = (data: ITerminalLinkParameter): ITerminalLinkParameter => {
  return {
    dataType: (data?.dataType as string) || '',
    id: (data?.id as number) || 0,
    name: (data?.name as string) || '',
    value: (data?.value as string) || '',
  };
};

export const normalizeTerminalLink = (data: ITerminalLinkData): ITerminalLink => {
  return {
    bankTerminal: {
      bank: {
        adapterCode: (data?.bankTerminal?.bank?.adapterCode as string) || '',
        id: (data?.bankTerminal?.bank?.id as number) || 0,
        name: (data?.bankTerminal?.bank?.name as string) || '',
        paymentMethod: (data?.bankTerminal?.bank?.paymentMethod as string) || '',
      },
      id: (data?.bankTerminal?.id as number) || 0,
      name: (data?.bankTerminal?.name as string) || '',
    },
    cardholderOverrideMinLength: (data?.cardholderOverrideMinLength as number) || 0,
    cardholderOverrideType:
      (data?.cardholderOverrideType as CARDHOLDER_OVERRIDE_TYPES) || '',
    checkStatusOnEarlyWebhook: !!data?.checkStatusOnEarlyWebhook,
    checkStatusOnEarlyWebhookDelaySeconds:
      (data?.checkStatusOnEarlyWebhookDelaySeconds as number) || null,
    customParametersDeposit: (data?.customParametersDeposit as string) || '',
    customParametersPayout: (data?.customParametersPayout as string) || '',
    description: (data?.description as string) || '',
    enabled: !!data?.enabled,
    exitIFrame: !!data?.exitIFrame,
    exitIFrameMode:
      (data?.exitIFrameMode as EXIT_IFRAME_MODE) || EXIT_IFRAME_MODE.CURRENT_TAB,
    id: data?.id as number,
    limitsEnabled: !!data?.limitsEnabled,
    merchantTerminal: {
      id: (data?.merchantTerminal?.id as number) || 0,
      merchant: {
        id: (data?.merchantTerminal?.merchant?.id as number) || 0,
        name: (data?.merchantTerminal?.merchant?.name as string) || '',
      },
      name: (data?.merchantTerminal?.name as string) || '',
    },
    motoEnabled: !!data?.motoEnabled,
    multipleFinalStates: !!data?.multipleFinalStates,
    parameters: data?.parameters?.map(normalizeParameters) || [],
    paymentMethod: (data?.paymentMethod as string) || '',
    payoutTimeout: (data?.payoutTimeout as number) || null,
    proxyUrl: (data?.proxyUrl as string) || null,
    pspSendPaymentTimeoutSeconds: (data?.pspSendPaymentTimeoutSeconds as number) || null,
    purchaseTimeout: (data?.purchaseTimeout as number) || null,
    recurringEnabled: !!data?.recurringEnabled,
    refundTimeout: (data?.refundTimeout as number) || null,
    settlementContract: {
      contractName: (data?.settlementContract?.contractName as string) || '',
      id: (data?.settlementContract?.id as number) || 0,
    },
    subPaymentMethod: (data?.subPaymentMethod as string) || null,
    terminalLinkGroups: data?.terminalLinkGroups?.map(normalizeTerminalLinkGroup) || [],
    threeDSEnabled: !!data?.threeDSEnabled,
    trusted: !!data?.trusted,
    uniqueCustomerId: (data?.uniqueCustomerId as UNIQUE_CUSTOMER_ID_TYPES) || '',
    useBankMessage: !!data?.useBankMessage,
    useCustomActions: !!data?.useCustomActions,
    useCustomReturnUrl: !!data?.useCustomReturnUrl,
    webhookIpWhitelistEnabled: !!data?.webhookIpWhitelistEnabled,
    webhookProxyUrl: (data?.webhookProxyUrl as string) || null,
    webhooksEnabled: !!data?.webhooksEnabled,
    whitelistIps: ((data?.whitelistIps as string)?.split(';') as string[]) || null,
  };
};

export const normalizeTerminalLinkLimits = (data: TObject): ITerminalLinkLimit => {
  return {
    actualAmount: (data?.actualAmount as number) || 0,
    actualCount: (data?.actualCount as number) || 0,
    amount: data?.amount as number,
    cardBrand: (data?.cardBrand as string) || '',
    count: data?.count as number,
    countOf: (data?.countOf as COUNT_OF) || COUNT_OF.TRANSACTIONS,
    currency: (data?.currency as string) || '',
    customPeriod: (data?.customPeriod as number) || null,
    enabled: !!data?.enabled,
    id: (data?.id as number) || 0,
    issuingOrg: data?.issuingOrg
      ? {
          operation: (data.issuingOrg as TObject).operation as ISSUING_ORG_OPERATION,
          valuesList: ((data.issuingOrg as TObject).valuesList as string[]) || [],
        }
      : null,
    lastUpdated: (data?.lastUpdated as string) || '',
    limitType: (data?.limitType as LIMIT_TYPE) || '',
    periodCalculationType: (data?.periodCalculationType as CALCULATION_VARIANTS) || '',
    periodOffset: (data?.periodOffset as string) || '',
    periodType: (data?.periodType as string) || '',
    transactionStatus: (data?.transactionStatus as TRANSACTION_STATUS) || '',
    transactionType: (data?.transactionType as string) || '',
    uniqueCustomer: !!data?.uniqueCustomer,
    uniqueCustomerId: (data?.uniqueCustomerId as UNIQUE_CUSTOMER_ID_TYPES) || '',
    uniqueInstrument: !!data?.uniqueInstrument,
  };
};

const normalizeLink = (
  data: TObject,
  {
    disabledText,
    enabledText,
    showWithId,
    showWithMerchant,
    showWithStateFlag,
  }: IGetTerminalLinksConfig,
): ITrustedSelectOption => {
  const getLabel = () => {
    let label = (data?.description as string) || '';
    if (showWithId) {
      label = showWithId === 'after' ? `${label} (${data?.id})` : `${data?.id} ${label}`;
    }
    if (showWithMerchant && data?.merchant) {
      label = `${label} (${data.merchant as string})`;
    }
    if (showWithStateFlag) {
      label = `${label} - ${data?.enabled ? enabledText : disabledText}`;
    }
    return label;
  };

  return {
    isDisabled: !data?.enabled,
    label: getLabel(),
    limitsCount: (data?.limitsCount as number) ?? null,
    merchant: (data?.merchant as string) || '',
    paymentMethod: (data?.paymentMethod as string) || '',
    psp: (data?.psp as string) || '',
    trusted: !!data?.trusted,
    uniqueCustomerId: (data?.uniqueCustomerId as UNIQUE_CUSTOMER_ID_TYPES) || null,
    value: (data?.id as string) || '',
  };
};

export const normalizeLinks = (
  data: TResult,
  config: IGetTerminalLinksConfig,
): IGetTLGroupTerminalLinksResult => {
  const terminalLinks: IGetTLGroupTerminalLinksResult['terminalLinks'] = [];
  const terminalLinksMap: IGetTLGroupTerminalLinksResult['terminalLinksMap'] = {};

  data?.result?.forEach((item) => {
    const normalizedLink = normalizeLink(item, config);
    terminalLinks.push(normalizedLink);
    terminalLinksMap[normalizedLink.value as number] = normalizedLink;
  });

  return { terminalLinks, terminalLinksMap };
};

export const normalizeTerminalLinksLimits = (
  data: ITerminalLinksLimitsData,
): ITerminalLinksLimits => {
  return {
    ...normalizeTerminalLinkLimits(data),
    terminalLink: {
      bankTerminal: {
        bank: {
          id: (data?.terminalLink?.bankTerminal?.bank?.id as number) || 0,
          name: (data?.terminalLink?.bankTerminal?.bank?.name as string) || '',
          paymentMethod:
            (data?.terminalLink?.bankTerminal?.bank?.paymentMethod as string) || '',
        },
        id: (data?.terminalLink?.bankTerminal?.id as number) || 0,
        name: (data?.terminalLink?.bankTerminal?.name as string) || '',
      },
      description: (data?.terminalLink?.description as string) || '',
      id: (data?.terminalLink?.id as number) || 0,
      merchantTerminal: {
        id: (data?.terminalLink?.merchantTerminal?.id as number) || 0,
        merchant: {
          id: (data?.terminalLink?.merchantTerminal?.merchant?.id as number) || 0,
          name: (data?.terminalLink?.merchantTerminal?.merchant?.name as string) || '',
        },
        name: (data?.terminalLink?.merchantTerminal?.name as string) || '',
      },
    },
  };
};

export const normalizeTerminalLinksParameters = (
  data: TObject,
): ITerminalLinksParameters => {
  return {
    bankId: data?.bankId as number,
    bankName: (data?.bankName as string) || '',
    bankTerminalId: data?.bankTerminalId as number,
    bankTerminalName: (data?.bankTerminalName as string) || '',
    comment: (data?.comment as string) || '',
    dataType: (data?.dataType as string) || '',
    defaultValue: (data?.defaultValue as string) || '',
    editable: !!data?.editable,
    level: (data?.level as string) || '',
    merchantId: data?.merchantId as number,
    merchantName: (data?.merchantName as string) || '',
    merchantTerminalId: data?.merchantTerminalId as number,
    merchantTerminalName: (data?.merchantTerminalName as string) || '',
    name: (data?.name as string) || '',
    paramId: data?.paramId as number,
    paymentMethod: (data?.paymentMethod as string) || '',
    terminalLinkDescription: (data?.terminalLinkDescription as string) || '',
    terminalLinkId: data?.terminalLinkId as number,
    terminalLinkParamValueId: (data?.terminalLinkParamValueId as number) || 0,
    value: (data?.value as string) || '',
  };
};

export const normalizeTerminalLinksGroups = (
  data: ITerminalGroupData,
): ITerminalLinksGroups => {
  return {
    enabled: !!data?.enabled,
    id: (data?.id as string) || '',
    limitsCount: data?.limitsCount as number,
    merchantId: data?.merchant?.id as number,
    merchantName: (data?.merchant?.name as string) || '',
    name: (data?.name as string) || '',
    terminalLinksCount: data?.terminalLinksCount as number,
  };
};

export const normalizeTLGroup = (data: ITerminalGroupData): ITerminalGroup => {
  return {
    enabled: !!data?.enabled,
    id: (data?.id as number) || 0,
    limitsCount: data?.limitsCount as number,
    merchant: {
      id: (data?.merchant?.id as number) || 0,
      name: (data?.merchant?.name as string) || '',
    },
    name: (data?.name as string) || '',
    terminalLinks: (data?.terminalLinks as number[]) || [],
    terminalLinksCount: data?.terminalLinksCount as number,
  };
};

export const normalizeTLGroupUniqueCustomerIdCounts = (
  data: TObject,
): ITLGroupUniqueCustomerIdCounts => {
  const counts = (data?.counts as TObject[]) || [];

  return {
    counts: counts.map((item) => ({
      count: (item?.count as number) || 0,
      uniqueCustomerId: (item?.uniqueCustomerId as UNIQUE_CUSTOMER_ID_TYPES) || null,
    })),
    total: (data?.total as number) || 0,
  };
};

export const normalizeTLGroupLimits = (data: TObject): ITerminalGroupLimits => {
  return {
    ...normalizeTerminalLinkLimits(data),
    groupId: data?.groupId as number,
  };
};

export const normalizeTLParametersNames = (data: TObject): ITLParametersNames => {
  return {
    dataType: (data?.dataType as string) || '',
    name: (data?.name as string) || '',
    paramId: (data?.paramId as number) || 0,
  };
};

export const normalizeDropDownValue = (data: IDropDownValue): IDropDownValue => {
  return {
    id: (data?.id as number) || 0,
    name: (data?.name as string) || '',
    value: (data?.value as string) || '',
  };
};

export const normalizeLocaleDropDownValue = (
  data: ILocalDropDownValue,
): ILocalDropDownValue => {
  return {
    locale: (data?.locale as string) || '',
    value: (data?.value as string) || '',
  };
};

export const normalizeRequiredParameter = (
  data: IRequiredParameterData,
  isIntegration?: boolean,
): IRequiredParameterView => {
  const params = {
    inputClass: (data?.inputClass as string) || '',
    inputType: (data?.inputType as string) || '',
    type: (data?.parameterType as string) || '',
  };

  if (isIntegration) {
    params.type =
      INTEGRATION_REQUIRED_PARAMETER_TYPE_MAP[
        data?.parameterType as INTEGRATION_REQUIRED_PARAMETER_TYPE
      ];
    params.inputType =
      INTEGRATION_REQUIRED_INPUT_TYPE_MAP[
        data?.inputType as INTEGRATION_REQUIRED_INPUT_TYPE
      ];
    params.inputClass =
      INTEGRATION_REQUIRED_INPUT_CLASS_MAP[
        data?.inputClass as INTEGRATION_REQUIRED_CLASS_TYPE
      ];
  }

  return {
    ...params,
    dropDownValues:
      data?.values.map(normalizeDropDownValue).sort((a, b) => a.id - b.id) || [],
    forcedHash: (data?.forcedHash as number) || null,
    forcedOverride: (data?.forcedOverride as string) || null,
    forcedOverrideType: (data?.forcedOverrideType as string) || null,
    forcedPreparePage: (data?.forcedPreparePage as string) || null,
    id: (data?.id as number) || 0,
    localInputValidation: {
      attributes: (data?.localInputValidation?.attributes as TObject) || {},
      defaultValue: (data?.localInputValidation?.defaultValue as string) || '',
      localData:
        data?.localInputValidation?.localData?.map(normalizeLocaleDropDownValue) || [],
    },
    localInputValidationMessage: {
      defaultValue: (data?.localInputValidationMessage?.defaultValue as string) || '',
      localData:
        data?.localInputValidationMessage?.localData?.map(normalizeLocaleDropDownValue) ||
        [],
    },
    localPlaceholder: {
      defaultValue: (data?.localPlaceholder?.defaultValue as string) || '',
      localData:
        data?.localPlaceholder?.localData?.map(normalizeLocaleDropDownValue) || [],
    },
    name: (data?.parameterName as string) || '',
    order: (data?.parameterOrder as number) || 0,
    overrideIfNotNull: (data?.overrideIfNotNull as string) || null,
    overrideIfNull: (data?.overrideIfNull as string) || null,
    parameterType: (data?.parameterNameType as string) || '',
    transactionType: (data?.transactionType as TRANSACTIONS_TYPE) || '',
    truncateIfNotValidOption: (data?.truncateIfNotValidOption as string) || null,
    truncateIfNotValidValue: (data?.truncateIfNotValidValue as number) || null,
  };
};

export const normalizeTerminalLinkRequiredParameters = (
  data: IRequiredParametersData,
): IRequiredParameters => {
  return { result: data?.result?.map((item) => normalizeRequiredParameter(item)) };
};

const normalizeTLSettlements = (
  settlementContract: INullable<TTLSettlementContractData>,
): INullable<ITLSettlementContract> => {
  if (!settlementContract) {
    return null;
  }
  return {
    contractName: (settlementContract?.contractName as string) || '',
    contractStartDate: (settlementContract?.contractStartDate as string) || '',
    id: (settlementContract?.id as number) || null,
    merchant: {
      enabled: !!settlementContract?.merchant?.enabled,
      id: (settlementContract?.merchant?.id as number) || 0,
      name: (settlementContract?.merchant?.name as string) || '',
    },
    paymentDelay: (settlementContract?.paymentDelay as number) || 0,
    paymentPeriod: (settlementContract?.paymentPeriod as string) || '',
    settlementCurrency: (settlementContract?.settlementCurrency as string) || '',
    settlementTimeZoneOffset:
      (settlementContract?.settlementTimeZoneOffset as string) || '',
    terminalLinksCount: (settlementContract?.terminalLinksCount as number) || 0,
  };
};

export const normalizeSettlementConfig = (
  config?: TTLSettlementConfigData,
): ITLSettlementConfig => {
  return {
    endDate: (config?.endDate as string) || null,
    fixedTransactionFee: (config?.fixedTransactionFee as number) || 0,
    fixedTransactionFeeCcy: (config?.fixedTransactionFeeCcy as string) || '',
    forceFixedTransactionFeeCcy: !!config?.forceFixedTransactionFeeCcy,
    id: (config?.id as number) || null,
    merchantDiscountRate: (config?.merchantDiscountRate as number) || 0,
    minTransactionFee: (config?.minTransactionFee as number) || 0,
    minTransactionFeeCcy: (config?.minTransactionFeeCcy as string) || '',
    rollingReserve: (config?.rollingReserve as number) || 0,
    startDate: (config?.startDate as string) || null,
    terminalLink: {
      description: (config?.terminalLink?.description as string) || '',
      enabled: !!config?.terminalLink?.enabled,
      id: (config?.terminalLink?.id as number) || 0,
      settlementContract: {
        contractName:
          (config?.terminalLink?.settlementContract?.contractName as string) || '',
        id: (config?.terminalLink?.settlementContract?.id as number) || 0,
        settlementCurrency:
          (config?.terminalLink?.settlementContract?.settlementCurrency as string) || '',
        settlementTimeZoneOffset:
          (config?.terminalLink?.settlementContract
            ?.settlementTimeZoneOffset as string) || '',
      },
    },
    transactionCurrency: (config?.transactionCurrency as string) || '',
    transactionState: (config?.transactionState as TRANSACTIONS_STATE) || '',
    transactionType: (config?.transactionType as TRANSACTIONS_TYPE) || '',
  };
};

export const normalizeTLSettlementConfigurations = (
  data: TObject,
): ITLSettlementConfigurations => {
  const configs = Array.isArray(data?.configs) ? data?.configs : [];
  const settlementContract = (data?.settlementContract ||
    null) as INullable<TTLSettlementContractData>;

  return {
    configs: configs.map(normalizeSettlementConfig),
    settlementContract: normalizeTLSettlements(settlementContract),
  };
};

export const normalizeTLAction = (data: TTLActionData): ITerminalLinkAction => {
  return {
    accuracyPercentage: (data?.accuracyPercentage as number) || null,
    actionType: (data?.actionType as string) || null,
    created: (data?.created as string) || null,
    enabled: !!data?.enabled,
    id: (data?.id as number) || null,
    name: (data?.name as string) || null,
    outputChannels:
      data?.outputChannels.map((data) => ({
        id: (data?.id as number) || null,
        name: (data?.name as string) || null,
      })) || [],
    stage: (data?.stage as string) || null,
    terminalLink: {
      id: (data?.terminalLink?.id as number) || null,
    },
    triggerType: (data?.triggerType as string) || null,
    updated: (data?.updated as string) || null,
  };
};

export const normalizeTerminalLinkIntegrationsRequiredParameters = (
  data: IRequiredParameterData[],
): IRequiredParameters => {
  return { result: data?.map((item) => normalizeRequiredParameter(item, true)) };
};

const HEALTH_STATUSES = Object.values(TERMINAL_LINK_HEALTH_STATUS);

// Optional keys are copied only when the BE sent them: a defaulted 0 would read as a real 0% rate.
const normalizeTerminalLinkHealthBlock = (data?: TObject): ITerminalLinkHealthBlock => {
  const status = data?.status as TERMINAL_LINK_HEALTH_STATUS;
  const counts = data?.counts as ITerminalLinkHealthCounts | undefined;
  const latency = data?.latency as ITerminalLinkHealthBlock['latency'];
  const parameters = data?.parameters as
    | ITerminalLinkHealthBlock['parameters']
    | undefined;

  return {
    ...(typeof data?.approvalRate === 'number' && { approvalRate: data.approvalRate }),
    asOf: (data?.asOf as string) || '',
    ...(counts && {
      counts: {
        abandoned: counts.abandoned || 0,
        approved: counts.approved || 0,
        attemptsStarted: counts.attemptsStarted || 0,
        declined: counts.declined || 0,
        latencySamples: counts.latencySamples || 0,
        nonAttributable: counts.nonAttributable || 0,
        pending: counts.pending || 0,
        samples: counts.samples || 0,
        timedOut: counts.timedOut || 0,
      },
    }),
    ...(typeof data?.lastAttemptAt === 'string' && { lastAttemptAt: data.lastAttemptAt }),
    ...(latency && {
      latency: { maxMs: latency.maxMs || 0, meanMs: latency.meanMs || 0 },
    }),
    parameters: {
      minSamples: parameters?.minSamples || 0,
      windowMinutes: parameters?.windowMinutes || 0,
    },
    status: HEALTH_STATUSES.includes(status)
      ? status
      : TERMINAL_LINK_HEALTH_STATUS.UNAVAILABLE,
    windowFrom: (data?.windowFrom as string) || '',
    windowTo: (data?.windowTo as string) || '',
  };
};

export const normalizeTerminalLinkHealth = (
  data: ITerminalLinkHealthData,
): ITerminalLinkHealth => {
  return {
    baseline: normalizeTerminalLinkHealthBlock(data?.baseline),
    day: normalizeTerminalLinkHealthBlock(data?.day),
    recent: normalizeTerminalLinkHealthBlock(data?.recent),
    terminalLinkId: (data?.terminalLinkId as number) || 0,
  };
};

const toIdList = (value: unknown): number[] =>
  Array.isArray(value) ? value.map(Number).filter((id) => !Number.isNaN(id)) : [];

// Warnings may arrive as bare limit ids or as objects with a code and params;
// both collapse to the object form so the UI has one shape to render.
const normalizeCopyTLLimitsWarning = (data: unknown): ICopyTLLimitsWarning => {
  if (typeof data === 'number') {
    return { code: '', limitId: data, params: {} };
  }
  const raw = (data || {}) as TObject;

  return {
    code: (raw.code as string) || '',
    limitId: Number(raw.limitId) || 0,
    params: (raw.params as Record<string, number | string>) || {},
  };
};

const normalizeCopyTLLimitsTarget = (data: TObject): ICopyTLLimitsTarget => ({
  copied: toIdList(data?.copied),
  notCopiedCurrency: toIdList(data?.notCopiedCurrency),
  notCopiedInvalidSource: toIdList(data?.notCopiedInvalidSource),
  skipped: toIdList(data?.skipped),
  status: (data?.status as COPY_LIMITS_TARGET_STATUS) || COPY_LIMITS_TARGET_STATUS.FAILED,
  targetId: Number(data?.targetId) || 0,
  warnings: Array.isArray(data?.warnings)
    ? data.warnings.map(normalizeCopyTLLimitsWarning)
    : [],
});

export const normalizeCopyTLLimitsResult = (data?: TObject): ICopyTLLimitsResult => {
  const targets = ((data?.targets as TObject[]) || []).map(normalizeCopyTLLimitsTarget);

  // Totals are derived from the per-target lists so the UI never depends on the
  // backend aggregate shape.
  const totals = targets.reduce(
    (acc, target) => ({
      copied: acc.copied + target.copied.length,
      notCopiedCurrency: acc.notCopiedCurrency + target.notCopiedCurrency.length,
      notCopiedInvalidSource:
        acc.notCopiedInvalidSource + target.notCopiedInvalidSource.length,
      skipped: acc.skipped + target.skipped.length,
      warnings: acc.warnings + target.warnings.length,
    }),
    {
      copied: 0,
      notCopiedCurrency: 0,
      notCopiedInvalidSource: 0,
      skipped: 0,
      warnings: 0,
    },
  );

  return { targets, totals };
};
